import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema
}: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const fullUrl = `${siteUrl}${location.pathname}`;
  const defaultImage = 'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg';

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:image', ogImage || defaultImage, true);
    updateMetaTag('og:type', 'website', true);

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage || defaultImage);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical || fullUrl;

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    // Update last-modified
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);
  }, [title, description, keywords, canonical, ogImage, schema, fullUrl, siteUrl]);
}

// Schema.org generators
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: 'Leury Tours',
    description: 'Traslados profesionales al aeropuerto y tours en Puerto Plata, República Dominicana',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg',
    image: 'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg',
    telephone: '+1-809-461-9001',
    email: 'leury2124@gmail.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Puerto Plata',
      addressCountry: 'DO'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '19.7892',
      longitude: '-70.6869'
    },
    sameAs: [
      'https://www.instagram.com/leury2124',
      'https://www.facebook.com/share/1VPJPAepMd/'
    ],
    priceRange: '$$',
    areaServed: {
      '@type': 'City',
      name: 'Puerto Plata'
    }
  };
};

export const generateTourSchema = (tour: {
  name: string;
  description: string;
  price: number;
  duration: string;
  image: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: tour.name,
    description: tour.description,
    image: tour.image,
    provider: {
      '@type': 'TravelAgency',
      name: 'Leury Tours',
      url: siteUrl
    },
    offers: {
      '@type': 'Offer',
      price: tour.price,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}/tours`
    },
    duration: tour.duration,
    touristType: 'Families, Couples, Groups'
  };
};

export const generateServiceSchema = (service: {
  name: string;
  description: string;
  price: number;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.name,
    description: service.description,
    provider: {
      '@type': 'TravelAgency',
      name: 'Leury Tours',
      url: siteUrl
    },
    areaServed: {
      '@type': 'City',
      name: 'Puerto Plata'
    },
    offers: {
      '@type': 'Offer',
      price: service.price,
      priceCurrency: 'USD'
    }
  };
};

export const generateWebPageSchema = (page: {
  name: string;
  description: string;
  url: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.name,
    description: page.description,
    url: page.url,
    inLanguage: 'es',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Leury Tours',
      url: import.meta.env.VITE_SITE_URL || 'https://example.com'
    }
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};
